import { useSearchParams } from 'react-router-dom';
import { useOtpValidateMutation } from 'src/hooks/mutations/otp';
import { useUserStore } from 'src/hooks/stores';
import { useRef, useEffect, useCallback } from 'react';
import { useChakra } from '@chakra-ui/react';
import { OtpValidateResponse } from '../utils/api';
import { isOtpNotRequiredError } from '../utils/otpHelpers';
import { useMeQuery } from 'src/hooks/queries/user';

type UseOtpLinkValidationOptions = {
	onSuccess?: (response: OtpValidateResponse) => void;
	onError?: (error: any) => void;
	onMutate?: () => void;
	onSettled?: () => void;
	successMessage?: string;
	alreadyValidatedMessage?: string;
	errorMessage?: string;
	checkMeQueryStatus?: boolean; 
	validateAlways?: boolean; 
};


export const useOtpLinkValidation = (options?: UseOtpLinkValidationOptions) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { setUser, email, name, token, userStatus, signupSource, id } = useUserStore();
	const hasValidatedFromUrl = useRef(false);
	const { theme, colorMode, setColorMode, toggleColorMode } = useChakra();

	const otpFromUrl = searchParams.get('otp');
	const emailFromUrl = searchParams.get('email');
	const needsOtpValidation = !!(otpFromUrl && emailFromUrl);
	const meQuery = useMeQuery({
		enabled: !!options?.checkMeQueryStatus && needsOtpValidation,
	});
	const isNotLoggedIn = !token;
	const isPendingOtp = userStatus === 'otp_pending';
	
	// Quando há OTP na URL, useMeQuery está desabilitado, então sempre usa userStatus do store
	const effectiveUserStatus = needsOtpValidation 
		? userStatus 
		: (options?.checkMeQueryStatus ? (meQuery.data?.userStatus || userStatus) : userStatus);
	
	const shouldValidateOtp = options?.validateAlways
		? needsOtpValidation
		: needsOtpValidation && (isNotLoggedIn || isPendingOtp);

	const clearOtpParams = useCallback(() => {
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.delete('otp');
		newSearchParams.delete('email');
		setSearchParams(newSearchParams, { replace: true });
	}, [searchParams, setSearchParams]);

	const showToast = useCallback(
		async (toastOptions: { description: string; status: 'info' | 'warning' | 'error' }) => {
			const { createStandaloneToast } = await import(
				/* webpackChunkName: "Toast" */
				'src/hooks/useToast'
			);

			const toast = createStandaloneToast({
				theme,
				colorMode,
				setColorMode,
				toggleColorMode,
			} as any);

			toast(toastOptions);
		},
		[colorMode, setColorMode, theme, toggleColorMode],
	);

	const validateFromUrlMutation = useOtpValidateMutation({
		onMutate: () => {
			options?.onMutate?.();
		},
		onSuccess: (validateResponse) => {
			const currentState = useUserStore.getState();
			clearOtpParams();

			setUser({
				id: id || validateResponse.userId || currentState.id || 0,
				email: emailFromUrl || email || validateResponse.email || '',
				name: name || validateResponse.name || '',
				token: validateResponse.token || token,
				userStatus: validateResponse.status || 'active',
				signupSource: signupSource || currentState.signupSource || '', // Preserva signupSource original do backend
			});

			const successMsg = options?.successMessage || 'Código validado com sucesso!';
			void showToast({
				description: successMsg,
				status: 'info',
			});

			options?.onSuccess?.(validateResponse);
		},
		onError: (error: any) => {
			console.error('[OTP Link Validation] Failed:', error);
			clearOtpParams();

			if (isOtpNotRequiredError(error)) {
				useUserStore.getState().setUserStatus('active');
			} else {
				const errorMsg = options?.errorMessage || 'Código inválido ou expirado. Tente novamente.';
				void showToast({
					description: errorMsg,
					status: 'error',
				});
			}

			options?.onError?.(error);
		},
		onSettled: () => {
			options?.onSettled?.();
		},
	});

	useEffect(() => {
		if (!needsOtpValidation) return;

		if (shouldValidateOtp && !hasValidatedFromUrl.current && !validateFromUrlMutation.isPending) {
			if (options?.validateAlways && !isNotLoggedIn && effectiveUserStatus !== 'otp_pending') {
				const alreadyValidatedMsg = options?.alreadyValidatedMessage || 'Esse usuário já está validado';
				clearOtpParams();
				void showToast({
					description: alreadyValidatedMsg,
					status: 'info',
				});
				hasValidatedFromUrl.current = true;
				return;
			}

			hasValidatedFromUrl.current = true;
			validateFromUrlMutation.mutate({
				otp: otpFromUrl!,
				email: emailFromUrl!,
				auth: true,
			});
		} else if (!shouldValidateOtp && !hasValidatedFromUrl.current) {
			hasValidatedFromUrl.current = true;
			clearOtpParams();
		}
	}, [shouldValidateOtp, needsOtpValidation, otpFromUrl, emailFromUrl, validateFromUrlMutation, clearOtpParams, options?.validateAlways, options?.alreadyValidatedMessage, isNotLoggedIn, effectiveUserStatus, showToast]);

	const isProcessing = needsOtpValidation && (validateFromUrlMutation.isPending || !hasValidatedFromUrl.current);

	return { isProcessing };
};
